import { KeyboardEvent, useEffect, useState } from 'react';
import { CLPrimaryCustomButton, CLPrimaryInput } from '../ui-controls';

import axios, { AxiosError } from 'axios';

import CommonConstants from '@/constants/common-constants';
import { isValidEmail } from '@/services/utils/utils.service';
import { LoadingSpinWhiteCustom } from '../utils/loading.component';

declare global {
  interface Window {
    onloadTurnstileCallback: any;
    turnstile: any;
  }
}

interface SignUpProps {
  redirectTo?: string;
  // When set, "Sign in" switches the view in place instead of navigating
  onSwitchView?: () => void;
}

export default function SignUpComponent({
  redirectTo,
  onSwitchView
}: SignUpProps) {
  // Variables

  // States
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [isEmailLoading, setEmailLoading] = useState<boolean>(false);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [isVerificationEmailSent, setIsVerificationEmailSent] =
    useState<boolean>(false);
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);

  useEffect(() => {
    window.onloadTurnstileCallback = () => {
      window.turnstile.render('#cl-turnstile-container', {
        sitekey: process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY,
        callback: (token: string) => {
          setTurnstileToken(token);
        }
      });
    };
  }, []);

  function refreshTurnstileToken() {
    if (window.turnstile) {
      window.turnstile.reset('#cl-turnstile-container');
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      signUp();
    }
  };

  // Helper: list missing complexity requirements
  function getPasswordStrengthIssues(password: string): string[] {
    const issues: string[] = [];

    if (!/[A-Z]/.test(password)) {
      issues.push('Include an uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      issues.push('Include a lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
      issues.push('Include a number');
    }
    if (!/[^\w\s]/.test(password)) {
      issues.push('Include a special character');
    }

    return issues;
  }

  async function signUp() {
    let turnstileToken;

    // Reset
    setErrorMsg('');
    setSuccessMsg('');

    // Validation
    if (!email) {
      setErrorMsg('Please enter your email address');
      document.getElementById('sign_up_email_field')?.focus();
      return;
    } else if (!isValidEmail(email)) {
      setErrorMsg('Please enter a valid email address');
      document.getElementById('sign_up_email_field')?.focus();
      return;
    } else if (!password) {
      setErrorMsg('Please enter your password');
      document.getElementById('sign_up_password_field')?.focus();
      return;
    } else if (password.length < 12) {
      setErrorMsg('Password must be at least 12 characters long');
      document.getElementById('sign_up_password_field')?.focus();
      return;
    } else {
      // Collect all password issues:
      const issues: string[] = [];
      // 2) Complexity requirements
      const complexityIssues = getPasswordStrengthIssues(password);
      issues.push(...complexityIssues);

      if (issues.length > 0) {
        // Build a multiline message
        const listLines = issues.map(i => `- ${i}`).join('\n');
        const message = `Password must:\n ${listLines}`;
        setErrorMsg(message);

        document.getElementById('sign_up_password_field')?.focus();
        return;
      }
    }

    // Turnstile validation
    if (!window.turnstile) {
      setErrorMsg('Turnstile is not yet loaded.');
      return;
    } else {
      turnstileToken = window.turnstile.getResponse('#cl-turnstile-container');

      if (!turnstileToken) {
        setErrorMsg(
          'Turnstile token is not found. Please retry in few seconds.'
        );
        return;
      }
    }

    // Start email loading
    setEmailLoading(true);

    // Refresh the token always, since current token will be used in the following request
    refreshTurnstileToken();

    // Send magic-link
    try {
      const response = await axios.post(
        CommonConstants.API_PREFIX + '/login/email-password',
        {
          email,
          password,
          redirectTo:
            (process.env.NEXT_PUBLIC_PRODUCTION === 'true'
              ? 'https://'
              : 'http://') +
            document.location.host +
            (redirectTo || '/projects')
        },
        {
          headers: {
            Authorization: 'Bearer ' + turnstileToken
          }
        }
      );
      setSuccessMsg(
        "Please check your inbox and verify your email to proceed. <br /> In case you don't see email in Inbox, please check Spam and mark it Not Spam"
      );
      setIsVerificationEmailSent(true);
    } catch (error) {
      if (error instanceof AxiosError) {
        setErrorMsg(error?.response?.data?.error?.message || error.message);
      } else {
        setErrorMsg('Something Went wrong. Please try again');
      }
    }

    // Stop email loading
    setEmailLoading(false);
  }

  return (
    <div className="mt-8">
      <div className="space-y-2">
        <div>
          <CLPrimaryInput
            id="sign_up_email_field"
            type="email"
            label="Email *"
            value={email}
            onKeyDown={handleKeyDown}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setEmail(event.target.value);
            }}
          />
        </div>
        <div className="relative">
          <CLPrimaryInput
            id="sign_up_password_field"
            type={passwordVisible ? 'text' : 'password'}
            label="Password *"
            value={password}
            onKeyDown={handleKeyDown}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setPassword(event.target.value);
            }}
          />
          {password && (
            <button
              type="button"
              className="absolute right-3 top-[68%] transform -translate-y-1/2 text-sm text-gray-700"
              onClick={() => setPasswordVisible(!passwordVisible)}
            >
              {passwordVisible ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a10.05 10.05 0 012.504-3.592m2.882-1.892A9.955 9.955 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.954 9.954 0 01-4.032 5.354M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 3l18 18"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7S3.732 16.057 2.458 12z"
                  />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Turnstile container */}
      <div id="cl-turnstile-container"></div>

      {/* Error message */}
      {errorMsg && (
        <div className="mt-2 text-red-400 whitespace-pre-line">{errorMsg}</div>
      )}

      {/* Success message */}
      {successMsg && (
        <div
          className="mt-2 text-black"
          dangerouslySetInnerHTML={{ __html: successMsg }}
        ></div>
      )}

      <div className="mt-4 w-full">
        <CLPrimaryCustomButton
          className="w-full text-base px-3 py-3"
          onClick={() => {
            signUp();
          }}
          disabled={
            isEmailLoading || !turnstileToken || isVerificationEmailSent
          }
        >
          {isEmailLoading && (
            <LoadingSpinWhiteCustom className="w-5 h-5"></LoadingSpinWhiteCustom>
          )}
          Create new account
        </CLPrimaryCustomButton>
      </div>

      {/* Go to login screen */}
      <div className="flex items-center justify-center mt-4">
        <span className="text-black/[0.7]">Already have an account?</span>
        {/* Add extra space here */}
        <span className="mx-1"></span>
        <a
          href="login"
          onClick={event => {
            if (onSwitchView) {
              event.preventDefault();
              onSwitchView();
            }
          }}
        >
          Sign in.
        </a>
      </div>
    </div>
  );
}
